import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Select, MenuItem, FormControl, InputLabel,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, CircularProgress, Alert, Tooltip, Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import GridOnIcon from '@mui/icons-material/GridOn';
import { principalAPI } from '@services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const PERIODS = [1, 2, 3, 4, 5, 6, 7, 8];

const DAY_BG = ['#eff6ff', '#f0fdf4', '#fdf4ff', '#fff7ed', '#f0f9ff', '#fef9c3'];

export default function ClassTimetable() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [slots, setSlots] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [slotForm, setSlotForm] = useState({ subject_id: '', teacher_id: '', start_time: '', end_time: '' });

  useEffect(() => {
    principalAPI.getClasses().then(r => setClasses(r.data.classes || [])).catch(() => {});
    principalAPI.getTeachers().then(r => setTeachers(r.data.teachers || [])).catch(() => {});
  }, []);

  const fetchTimetable = useCallback(async (classId) => {
    if (!classId) return;
    setLoading(true);
    setError('');
    try {
      const res = await principalAPI.getTimetable(classId);
      setSlots(res.data.slots || []);
      setSubjects(res.data.subjects || []);
    } catch {
      setError('Failed to load timetable');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTimetable(selectedClass);
  }, [selectedClass, fetchTimetable]);

  const getSlot = (day, period) =>
    slots.find(s => s.day_of_week === day && s.period_number === period);

  const openEdit = (day, period) => {
    const existing = getSlot(day, period);
    setEditSlot({ day, period });
    setSlotForm({
      subject_id: existing?.subject_id || '',
      teacher_id: existing?.teacher_id || '',
      start_time: existing?.start_time || '',
      end_time: existing?.end_time || '',
    });
    setDialogOpen(true);
  };

  const handleSaveSlot = async () => {
    if (!slotForm.subject_id) return;
    setSaving(true);
    try {
      const subj = subjects.find(s => s.id === slotForm.subject_id);
      await principalAPI.upsertTimetableSlot({
        class_id: selectedClass,
        subject_id: slotForm.subject_id || null,
        teacher_id: slotForm.teacher_id || null,
        day_of_week: editSlot.day,
        period_number: editSlot.period,
        start_time: slotForm.start_time || null,
        end_time: slotForm.end_time || null,
        subject_name: subj?.subject_name || null,
      });
      setDialogOpen(false);
      fetchTimetable(selectedClass);
    } catch {
      setError('Failed to save slot');
    } finally {
      setSaving(false);
    }
  };

  const handleClearSlot = async (day, period) => {
    try {
      await principalAPI.deleteTimetableSlot(selectedClass, day, period);
      fetchTimetable(selectedClass);
    } catch {
      setError('Failed to clear slot');
    }
  };

  const SlotCell = ({ day, period }) => {
    const slot = getSlot(day, period);
    const displayName = slot?.subject_name || slot?.subject_name_from_table || '';
    const teacherName = slot?.teacher_name || '';

    return (
      <Box
        onClick={() => openEdit(day, period)}
        sx={{
          minHeight: 64,
          border: '1px solid',
          borderColor: slot ? 'primary.light' : 'grey.200',
          borderRadius: 1,
          p: 0.75,
          cursor: 'pointer',
          bgcolor: slot ? '#e8f4fd' : '#fafafa',
          position: 'relative',
          transition: 'all 0.15s',
          '&:hover': { bgcolor: slot ? '#dbeafe' : '#f0f9ff', borderColor: 'primary.main' },
        }}
      >
        {slot ? (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.dark', display: 'block', lineHeight: 1.2 }}>
              {displayName}
            </Typography>
            {teacherName && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block' }}>
                {teacherName}
              </Typography>
            )}
            {slot.start_time && (
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem', display: 'block' }}>
                {slot.start_time}–{slot.end_time || '?'}
              </Typography>
            )}
            <IconButton
              size="small"
              sx={{ position: 'absolute', top: 2, right: 2, p: 0.2, opacity: 0, '&:hover': { opacity: 1 }, '.MuiBox-root:hover &': { opacity: 0.6 } }}
              onClick={(e) => { e.stopPropagation(); handleClearSlot(day, period); }}
            >
              <DeleteIcon sx={{ fontSize: 12 }} />
            </IconButton>
          </>
        ) : (
          <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.65rem' }}>Click to assign</Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <GridOnIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>Timetable Builder</Typography>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Select Class</InputLabel>
            <Select value={selectedClass} label="Select Class" onChange={e => setSelectedClass(e.target.value)}>
              {classes.map(c => (
                <MenuItem key={c.id} value={c.id}>{c.name} – Grade {c.grade}{c.section}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedClass && (
            <Chip label={`${slots.length} slots assigned`} size="small" color="primary" variant="outlined" />
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {!selectedClass ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
            <GridOnIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
            <Typography color="text.secondary">Select a class to view or edit its timetable</Typography>
          </Paper>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <Paper sx={{ borderRadius: 2, overflow: 'auto' }}>
            <Box sx={{ minWidth: 700 }}>
              {/* Header row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '80px repeat(6, 1fr)', bgcolor: '#1e293b', color: '#fff' }}>
                <Box sx={{ p: 1.5, textAlign: 'center' }}>
                  <Typography variant="caption" fontWeight={700}>Period</Typography>
                </Box>
                {DAYS.map((d, i) => (
                  <Box key={d} sx={{ p: 1.5, textAlign: 'center', bgcolor: i === 5 ? '#334155' : undefined }}>
                    <Typography variant="caption" fontWeight={700}>{d}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Period rows */}
              {PERIODS.map(period => (
                <Box key={period}
                  sx={{ display: 'grid', gridTemplateColumns: '80px repeat(6, 1fr)', borderBottom: '1px solid', borderColor: 'grey.100', '&:last-child': { borderBottom: 'none' } }}>
                  <Box sx={{ p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9', borderRight: '1px solid', borderColor: 'grey.200' }}>
                    <Chip label={`P${period}`} size="small" sx={{ fontWeight: 700, bgcolor: '#334155', color: '#fff', borderRadius: 1 }} />
                  </Box>
                  {DAYS.map((_, dayIdx) => (
                    <Box key={dayIdx} sx={{ p: 0.75, bgcolor: DAY_BG[dayIdx], borderRight: dayIdx < 5 ? '1px solid' : 'none', borderColor: 'grey.100' }}>
                      <SlotCell day={dayIdx} period={period} />
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {selectedClass && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Click any cell to assign a subject. Click the delete icon on a filled cell to clear it.</Typography>
          </Box>
        )}
      </Box>

      {/* Edit Slot Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={700}>
            {editSlot ? `${DAYS[editSlot.day]} – Period ${editSlot.period}` : ''}
          </Typography>
          <IconButton size="small" onClick={() => setDialogOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Subject *</InputLabel>
            <Select value={slotForm.subject_id} label="Subject *" onChange={e => {
              const subj = subjects.find(s => s.id === e.target.value);
              setSlotForm(f => ({
                ...f, subject_id: e.target.value,
                teacher_id: subj?.teacher_id || f.teacher_id,
              }));
            }}>
              {subjects.length === 0 && (
                <MenuItem disabled value=""><em>No subjects added to this class</em></MenuItem>
              )}
              {subjects.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.subject_name}{s.subject_code ? ` (${s.subject_code})` : ''}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Teacher</InputLabel>
            <Select value={slotForm.teacher_id} label="Teacher" onChange={e => setSlotForm(f => ({ ...f, teacher_id: e.target.value }))}>
              <MenuItem value=""><em>None</em></MenuItem>
              {teachers.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">Start Time</Typography>
              <input type="time" value={slotForm.start_time} onChange={e => setSlotForm(f => ({ ...f, start_time: e.target.value }))}
                style={{ display: 'block', width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">End Time</Typography>
              <input type="time" value={slotForm.end_time} onChange={e => setSlotForm(f => ({ ...f, end_time: e.target.value }))}
                style={{ display: 'block', width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 14 }} />
            </Box>
          </Box>
        </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSlot} disabled={saving || !slotForm.subject_id}>
            {saving ? 'Saving…' : 'Save Slot'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
